/**
 * App shell: navigation, the offline banner, and routing.
 *
 * Nav entries are filtered by permission, so a staff user sees the Sell tab
 * and nothing they cannot use. The backend enforces the same rules.
 */

import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useState } from 'react';

import { useSession } from './lib/session';
import LoginPage from './pages/LoginPage';
import SellPage from './pages/SellPage';
import ItemsPage from './pages/ItemsPage';
import OrdersPage from './pages/OrdersPage';
import ContactsPage from './pages/ContactsPage';
import CreditPage from './pages/CreditPage';
import EbayPage from './pages/EbayPage';
import AdminPage from './pages/AdminPage';
import UpdatePrompt from './components/UpdatePrompt';

interface NavEntry {
  to: string;
  label: string;
  permission?: string;
}

/** Which component each nav entry renders. Kept beside NAV so the two cannot
 *  drift apart and leave a route with no page (or a page with no guard). */
const PAGES: Record<string, JSX.Element> = {
  '/sell': <SellPage />,
  '/orders': <OrdersPage />,
  '/items': <ItemsPage />,
  '/credit': <CreditPage />,
  '/contacts': <ContactsPage />,
  '/ebay': <EbayPage />,
  '/admin': <AdminPage />,
};

const NAV: NavEntry[] = [
  { to: '/sell', label: 'Sell', permission: 'sell' },
  { to: '/orders', label: 'Orders', permission: 'sell' },
  { to: '/items', label: 'Items', permission: 'item.edit' },
  { to: '/credit', label: 'Credit', permission: 'trade' },
  { to: '/contacts', label: 'Payouts', permission: 'contacts' },
  { to: '/ebay', label: 'eBay', permission: 'ebay' },
  { to: '/admin', label: 'Admin', permission: 'admin' },
];

export default function App() {
  const { user, loading, online, pending, signOut, can, sync } = useSession();
  const [syncMessage, setSyncMessage] = useState('');

  if (loading) {
    return (
      <div className="centered">
        <p>Loading…</p>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const visible = NAV.filter((entry) => !entry.permission || can(entry.permission));
  const landing = visible[0]?.to ?? '/sell';

  async function handleSync() {
    setSyncMessage('Syncing…');
    setSyncMessage(await sync());
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">Convention POS</div>
        <nav className="nav">
          {visible.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            >
              {entry.label}
            </NavLink>
          ))}
        </nav>
        <div className="session">
          <span className="who">
            {user.name} <span className="role">{user.role}</span>
          </span>
          <button type="button" className="ghost" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>

      {/* The operator has to be able to tell at a glance whether a sale
          actually reached the server. */}
      {(!online || pending > 0) && (
        <div className={online ? 'banner pending' : 'banner offline'}>
          <span>
            {online
              ? `${pending} change${pending === 1 ? '' : 's'} waiting to sync.`
              : `Offline. Sales are saved on this device${
                  pending > 0 ? ` (${pending} waiting)` : ''
                } and will sync when you reconnect.`}
          </span>
          {online && pending > 0 && (
            <button type="button" onClick={() => void handleSync()}>
              Sync now
            </button>
          )}
          {syncMessage && <span className="sync-message">{syncMessage}</span>}
        </div>
      )}

      <main className="app-main">
        {/* Routes are guarded, not just the nav links: typing /admin into
            the address bar as a staff user previously rendered the page and
            left it firing 403s instead of simply not being reachable. */}
        <Routes>
          <Route path="/" element={<Navigate to={landing} replace />} />
          {NAV.map((entry) => (
            <Route
              key={entry.to}
              path={entry.to}
              element={
                !entry.permission || can(entry.permission) ? (
                  PAGES[entry.to]
                ) : (
                  <Navigate to={landing} replace />
                )
              }
            />
          ))}
          <Route path="*" element={<Navigate to={landing} replace />} />
        </Routes>
      </main>

      <UpdatePrompt />
    </div>
  );
}
